/**
 * Pure helpers + React Query cache keys for direct (secret) messages.
 *
 * Kept free of react/react-native imports so they can be unit-tested and
 * imported from both the data hooks and the websocket handler without cycles.
 */
import type {
  DirectMessageResponse,
  DirectMessageThreadResponse,
  DirectMessageThreadSummary,
  MessageReactionResponse,
} from '@warcarp/api-types'

/** Cache key for the DM thread-summary list (drives unread badges). */
export const dmThreadsKey = (gameId: string) => ['games', gameId, 'dms'] as const
/** Cache key for a single conversation with `otherUserId`. */
export const dmThreadKey = (gameId: string, otherUserId: string) =>
  ['games', gameId, 'dms', otherUserId] as const

/**
 * Given a DM and the current user's id, return the id of the OTHER participant
 * (the conversation partner). Works whether the message was sent or received.
 */
export function dmPartnerId(msg: DirectMessageResponse, meId: string): string {
  return msg.fromUserId === meId ? msg.toUserId : msg.fromUserId
}

/**
 * Append an incoming DM to a cached thread, deduping by id (the WS echo can
 * race the POST response, and a reconnect can replay). Seeds the thread when
 * the cache is empty so a live message shows even before the first fetch.
 */
export function appendDirectMessage(
  old: DirectMessageThreadResponse | undefined,
  incoming: DirectMessageResponse,
): DirectMessageThreadResponse {
  if (!old) return { messages: [incoming] }
  if (old.messages.some((m) => m.id === incoming.id)) return old
  return { ...old, messages: [...old.messages, incoming] }
}

/** Replace one private message's reactions with the server's canonical list. */
export function updateDirectMessageReactions(
  old: DirectMessageThreadResponse | undefined,
  messageId: string,
  reactions: MessageReactionResponse[],
): DirectMessageThreadResponse | undefined {
  if (!old) return old
  let changed = false
  const messages = old.messages.map((message) => {
    if (message.id !== messageId) return message
    changed = true
    return { ...message, reactions }
  })
  return changed ? { ...old, messages } : old
}

/**
 * Map of otherUserId → unread count, for rendering per-player roster badges.
 * Only includes partners with at least one unread message.
 */
export function unreadByUser(threads: DirectMessageThreadSummary[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const t of threads) {
    if (t.unreadCount > 0) out[t.otherUserId] = t.unreadCount
  }
  return out
}

/** Total unread direct messages across all threads (drives the aggregate dot). */
export function totalUnread(threads: DirectMessageThreadSummary[]): number {
  return threads.reduce((n, t) => n + t.unreadCount, 0)
}
